import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, X, CheckCircle, Loader2 } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import {
  uploadImageToCloudinary,
  type CloudinaryResponse,
  type UploadProgress,
} from "@shared/services/cloudinaryApi";

export interface UploadedImage {
  id: string;
  url: string;
  publicId: string;
  fileName: string;
  alt?: string; // Texto alternativo para accesibilidad (preservado de imágenes existentes)
}

interface ImageUploadProps {
  onImagesUploaded: (images: UploadedImage[]) => void;
  onImageRemoved: (imageId: string) => void;
  uploadedImages: UploadedImage[];
  maxFiles?: number;
  maxSizeMb?: number; // megabytes, para UI sencilla
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImagesUploaded,
  onImageRemoved,
  uploadedImages,
  maxFiles = 10,
  maxSizeMb = 10,
}) => {
  const [uploadingImages, setUploadingImages] = useState<Map<string, UploadProgress>>(new Map());

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (uploadedImages.length + acceptedFiles.length > maxFiles) {
        alert(`Puedes subir máximo ${maxFiles} imágenes`);
        return;
      }

      for (const file of acceptedFiles) {
        const imageId = `temp-${Date.now()}-${Math.random()}`;
        setUploadingImages((prev) => new Map(prev).set(imageId, { loaded: 0, total: file.size, percentage: 0 }));

        try {
          const response: CloudinaryResponse = await uploadImageToCloudinary(file, (progress) => {
            setUploadingImages((prev) => new Map(prev).set(imageId, progress));
          });

          const uploadedImage: UploadedImage = {
            id: imageId,
            url: response.secure_url,
            publicId: response.public_id,
            fileName: file.name,
            alt: "", // Alt vacío por defecto para nuevas imágenes
          };

          onImagesUploaded([...uploadedImages, uploadedImage]);
          setUploadingImages((prev) => {
            const copy = new Map(prev);
            copy.delete(imageId);
            return copy;
          });
        } catch (error) {
          setUploadingImages((prev) => {
            const copy = new Map(prev);
            copy.delete(imageId);
            return copy;
          });
          alert(error instanceof Error ? error.message : "Error al subir la imagen");
        }
      }
    },
    [uploadedImages, maxFiles, onImagesUploaded],
  );

  const maxSizeBytes = maxSizeMb * 1024 * 1024;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"] },
    maxSize: maxSizeBytes,
    multiple: true,
  });

  return (
    <div className="flex items-start gap-4">
      {/* Botón/área pequeña de subida */}
      <div
        {...getRootProps()}
        className={`inline-flex flex-col items-center justify-center border-2 border-dashed rounded-md px-4 py-4 text-center transition-colors cursor-pointer w-40 h-28 shrink-0 ${
          isDragActive ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="h-7 w-7 text-gray-400" />
        <p className="mt-1 text-[12px] text-gray-700 text-center leading-snug">
          {isDragActive ? "Suelta aquí" : "Subir imágenes"}
        </p>
        <p className="text-[11px] text-gray-500">máx. {maxSizeMb}MB</p>
      </div>

      {/* Listado horizontal de imágenes (subidas y en progreso) */}
      <div className="flex-1 min-w-0">
        {(uploadedImages.length > 0 || uploadingImages.size > 0) && (
          <Swiper
            spaceBetween={12}
            slidesPerView={3.2}
            breakpoints={{
              640: { slidesPerView: 3.5, spaceBetween: 12 },
              768: { slidesPerView: 4.2, spaceBetween: 12 },
              1024: { slidesPerView: 5.2, spaceBetween: 12 },
            }}
          >
            {Array.from(uploadingImages.entries()).map(([imageId, progress]) => (
              <SwiperSlide key={`progress-${imageId}`}>
                <div className="relative w-28 h-28 bg-gray-200 rounded-md flex items-center justify-center">
                  <Loader2 className="h-7 w-7 text-blue-500 animate-spin" />
                  <div className="absolute bottom-1 left-1 right-1">
                    <div className="bg-white rounded-full h-1.5 overflow-hidden">
                      <div className="bg-blue-500 h-full transition-all duration-300" style={{ width: `${progress.percentage}%` }} />
                    </div>
                    <p className="text-[10px] text-gray-600 mt-0.5 text-center">{progress.percentage}%</p>
                  </div>
                </div>
              </SwiperSlide>
            ))}

            {uploadedImages.map((image) => (
              <SwiperSlide key={image.id}>
                <div className="relative group w-28 h-28">
                  <img src={image.url} alt={image.fileName} className="w-28 h-28 object-cover rounded-md" />
                  <button
                    type="button"
                    onClick={() => onImageRemoved(image.id)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Eliminar imagen"
                  >
                    <X size={16} />
                  </button>
                  <div className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full p-0.5">
                    <CheckCircle size={16} />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </div>
  );
};


